import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedCenter, setSelectedCountry, setSelectedEvent } from '../redux/slices/eventSlice';
import eventService from '../services/eventService';

const EventSelection = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedCenter, selectedEvent, selectedCountry } = useSelector((state) => state.event);
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eventTimeOptions, setEventTimeOptions] = useState({});
  const [eventCoordinators, setEventCoordinators] = useState({});
  
  const fetchingRef = useRef(false);
  const dataLoadedRef = useRef(false);

  useEffect(() => {
    // Extract primitive values to avoid object reference changes
    const centerId = selectedCenter?.center_Id;
    const orgId = selectedCountry?.organizationId;
    
    console.log('EventSelection - centerId:', centerId, 'orgId:', orgId);

    // Prevent double API calls - only fetch if not already fetching and not already loaded
    if (fetchingRef.current || dataLoadedRef.current) {
      console.log('Skipping fetch - already fetching or loaded');
      return;
    }

    const fetchEvents = async () => {
      fetchingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        // Get the actual center_Id - check both Redux and sessionStorage
        let finalCenterId = centerId;
        let finalOrgId = orgId;
        
        // Fallback to sessionStorage if Redux is empty
        if (!finalCenterId) {
          const storedEventData = sessionStorage.getItem('yk_event_booking');
          if (storedEventData) {
            const eventData = JSON.parse(storedEventData);
            finalCenterId = eventData.selectedCenter?.center_Id;
            finalOrgId = eventData.selectedCountry?.organizationId;
          }
        }
        
        console.log('=== FETCHING EVENTS ===');
        console.log('centerId:', finalCenterId, 'organizationId:', finalOrgId);

        if (!finalCenterId) {
          try {
            const defaultCountryName = selectedCountry?.name || 'USA';

            if (!selectedCountry) {
              dispatch(setSelectedCountry({
                id: 1,
                name: defaultCountryName,
                entityCount: 0,
                organizationId: null
              }));
            }

            const centers = await eventService.getCenters(defaultCountryName);
            const firstCenter = Array.isArray(centers) ? centers[0] : null;

            if (firstCenter?.center_Id) {
              finalCenterId = firstCenter.center_Id;

              dispatch(setSelectedCenter({
                center_Id: firstCenter.center_Id,
                center_Name: firstCenter.center_Name,
                center_Logo_URL: firstCenter.center_Logo_URL,
                center_Desc: firstCenter.center_Desc,
                organization_Id: firstCenter.organization_Id,
                app_Id: firstCenter.app_Id,
              }));
            }
          } catch (e) {
            console.warn('Failed to select default center:', e);
          }
        }

        if (!finalCenterId) {
          console.error('No center_Id found!');
          setError('Center information missing. Please try again.');
          return;
        }
        
        const response = await eventService.getEventsByCenter(
          finalCenterId,
          finalOrgId || null
        );
        
        console.log('Events fetched:', response?.length || 0);
        
        // Sort events by priority field if available, otherwise by date, otherwise keep API order
        const sortedEvents = (response || []).sort((a, b) => {
          // If API provides display_Order or event_Priority, use that
          if (a.display_Order !== undefined && b.display_Order !== undefined) {
            return a.display_Order - b.display_Order;
          }
          if (a.event_Priority !== undefined && b.event_Priority !== undefined) {
            return a.event_Priority - b.event_Priority;
          }
          // Otherwise sort by event date (upcoming events first)
          if (a.event_Date && b.event_Date) {
            return new Date(a.event_Date) - new Date(b.event_Date);
          }
          return 0; // Keep original order if no sorting criteria
        });
        
        setEvents(sortedEvents);
        dataLoadedRef.current = true;
        
        // Fetch time options for all events
        console.log('=== FETCHING TIME OPTIONS FOR ALL EVENTS ===');
        const timeOptionsMap = {};
        for (const event of sortedEvents) {
          try {
            console.log(`Fetching time options for event: ${event.event_Name} (ID: ${event.event_Id})`);
            const timeOptions = await eventService.getEventTimeOptions(event.event_Id);
            console.log(`Time options received for ${event.event_Name}:`, timeOptions);
            if (timeOptions && timeOptions.length > 0) {
              timeOptionsMap[event.event_Id] = timeOptions;
              console.log(`✅ Stored ${timeOptions.length} time options for event ${event.event_Id}`);
            } else {
              console.log(`⚠️ No time options for event ${event.event_Id}`);
            }
          } catch (timeErr) {
            console.warn(`❌ Failed to fetch time options for event ${event.event_Id}:`, timeErr);
          }
        }
        setEventTimeOptions(timeOptionsMap);
        console.log('=== TIME OPTIONS FETCH COMPLETE ===');

        // Fetch coordinators for all events
        console.log('=== FETCHING COORDINATORS FOR ALL EVENTS ===');
        const coordinatorsMap = {};
        for (const event of sortedEvents) {
          try {
            console.log(`Fetching coordinators for event: ${event.event_Name} (ID: ${event.event_Id})`);
            const coordinators = await eventService.getEventCoordinators(event.event_Id);
            console.log(`Coordinators received for ${event.event_Name}:`, coordinators);
            if (coordinators && coordinators.length > 0) {
              coordinatorsMap[event.event_Id] = coordinators;
              console.log(`✅ Stored ${coordinators.length} coordinators for event ${event.event_Id}`);
            } else {
              console.log(`⚠️ No coordinators for event ${event.event_Id}`);
            }
          } catch (coordErr) {
            console.warn(`❌ Failed to fetch coordinators for event ${event.event_Id}:`, coordErr);
          }
        }
        setEventCoordinators(coordinatorsMap);
        console.log('=== COORDINATORS FETCH COMPLETE ===');
      } catch (err) {
        console.error('Error fetching events:', err);
        setError('Failed to load events. Please try again.');
        fetchingRef.current = false;
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEventSelect = (event) => {
    dispatch(setSelectedEvent({
      event_Id: event.event_Id,
      event_Name: event.event_Name,
      event_Description: event.event_Description,
      event_Date: event.event_Date,
      event_Time: event.event_Time,
      event_Image_URL: event.event_Image_URL,
      event_Location: event.event_Location,
      event_Capacity: event.event_Capacity,
      center_Id: event.center_Id,
    }));
    
    navigate('/event-info', { state: { scrollToFlyer: true } });
  };

  const handleRegister = async (event) => {
    dispatch(setSelectedEvent({
      event_Id: event.event_Id,
      event_Name: event.event_Name,
      event_Description: event.event_Description,
      event_Date: event.event_Date,
      event_Time: event.event_Time,
      event_Image_URL: event.event_Image_URL,
      event_Location: event.event_Location,
      event_Capacity: event.event_Capacity,
      center_Id: event.center_Id,
    }));

    navigate('/event-info?go=service');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getCoordinatorDetails = (event) => {
    if (!event) return [];

    const eventId = event?.event_Id;
    const coordinatorsList = eventCoordinators[eventId] || [];

    // Map coordinators from API to our format
    return coordinatorsList.map((c) => ({
      name: c?.coOrdinator_Name || c?.coordinator_Name || c?.name || c?.Name || '',
      email: c?.coOrdinator_Email || c?.coordinator_Email || c?.email || '',
      phone: c?.coOrdinator_Phone_Number || c?.coordinator_Phone || c?.phone || '',
      description: c?.coOrdinator_Description || c?.coordinator_Description || c?.description || ''
    })).filter(c => c.name); // Only include coordinators with names
  };

  const currentEvent = events?.[0] || null;
  const upcomingEvents = events?.length > 1 ? events.slice(1) : [];

  // Debug logging for time options and coordinators
  console.log('=== RENDER DEBUG ===');
  console.log('Current event:', currentEvent?.event_Name, 'ID:', currentEvent?.event_Id);
  console.log('eventTimeOptions state:', eventTimeOptions);
  console.log('Time options for current event:', eventTimeOptions[currentEvent?.event_Id]);
  if (eventTimeOptions[currentEvent?.event_Id]) {
    console.log('Time options details:', JSON.stringify(eventTimeOptions[currentEvent?.event_Id], null, 2));
  }
  console.log('=== COORDINATOR DEBUG ===');
  console.log('Current event object keys:', currentEvent ? Object.keys(currentEvent) : 'No event');
  console.log('Coordinator fields in event:', {
    coordinators: currentEvent?.coordinators,
    Coordinators: currentEvent?.Coordinators,
    event_Coordinators: currentEvent?.event_Coordinators,
    coordinator_Name: currentEvent?.coordinator_Name,
    coordinators_Name: currentEvent?.coordinators_Name
  });
  const coordDetails = getCoordinatorDetails(currentEvent);
  console.log('Extracted coordinator details:', coordDetails);
  console.log('==================');

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-indigo-50/70 via-white to-rose-50/70 pt-4 md:pt-6 pb-8 md:pb-10">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-purple-200/35 blur-3xl"></div>
        <div className="absolute top-10 -right-24 h-72 w-72 rounded-full bg-blue-200/35 blur-3xl"></div>
        <div className="absolute bottom-0 left-1/3 h-80 w-80 rounded-full bg-rose-200/25 blur-3xl"></div>
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 font-sans">
        <div className="flex items-center justify-between gap-4">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/70 px-3 py-1 text-[11px] font-semibold tracking-widest uppercase text-gray-600 shadow-sm border border-white/60">
            Yugalkunj Events
          </div>

          <button
            onClick={() => navigate('/center')}
            className="hidden flex items-center gap-2 rounded-full bg-white/80 hover:bg-white px-4 py-2 text-gray-700 font-semibold border border-white/70 shadow-sm hover:shadow-md transition-all duration-300"
            title="Back to Center Selection"
          >
            <i className="fas fa-arrow-left text-sm"></i>
            <span className="hidden sm:inline">Back</span>
          </button>
        </div>

        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-4 mb-8 max-w-2xl mx-auto">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-red-700 text-sm font-medium">{error}</p>
            </div>
          </div>
        )}

        {!loading && events.length > 0 && (
          <div className="mt-8">
            {currentEvent && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="relative overflow-hidden rounded-[28px] bg-white/75 backdrop-blur-xl shadow-[0_26px_70px_-45px_rgba(15,23,42,0.55)] border border-white/60">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/70 via-indigo-50/30 to-purple-50/30"></div>
                  <div className="relative p-6 md:p-8">
                    <div className="flex items-center justify-between gap-3">
                      <div className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-gray-700 shadow-sm border border-white/60">
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white border border-gray-200 shadow-sm">
                          <img
                            src="https://stssevastorage.blob.core.windows.net/ykwebsite/logo.png"
                            alt="Yugal Kunj"
                            className="h-4 w-auto"
                          />
                        </span>
                        <span>Yugalkunj Events</span>
                      </div>

                      <div className="inline-flex items-center rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-gray-700 shadow-sm border border-white/60">
                        Active
                      </div>
                    </div>

                    <h1 className="mt-6 font-display text-3xl md:text-5xl font-semibold tracking-tight text-gray-900 leading-[1.05]">
                      {currentEvent.event_Name}
                    </h1>

                    <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-gray-700">
                      {currentEvent.event_Date && (
                        <span className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 shadow-sm border border-white/60">
                          <i className="fa-solid fa-calendar-days text-indigo-600"></i>
                          <span className="font-medium">{formatDate(currentEvent.event_Date)}</span>
                        </span>
                      )}

                      {eventTimeOptions[currentEvent.event_Id]?.length > 0 ? (
                        eventTimeOptions[currentEvent.event_Id]
                          .filter(timeOption => timeOption.time_Option_Name?.toLowerCase() !== "not sure")
                          .map((timeOption, idx) => {
                          const formatTime = (dateTimeStr) => {
                            if (!dateTimeStr) return '';
                            const date = new Date(dateTimeStr);
                            return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                          };
                          const startTime = formatTime(timeOption.date_Time_From);
                          const endTime = formatTime(timeOption.date_Time_To);
                          
                          return (
                            <span key={idx} className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 shadow-sm border border-white/60">
                              <i className="fa-regular fa-clock text-purple-600"></i>
                              <span className="font-medium">
                                {timeOption.time_Option_Name}
                                {startTime && endTime && ` (${startTime} - ${endTime})`}
                              </span>
                            </span>
                          );
                        })
                      ) : currentEvent.event_Time && (
                        <span className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 shadow-sm border border-white/60">
                          <i className="fa-regular fa-clock text-purple-600"></i>
                          <span className="font-medium">{currentEvent.event_Time}</span>
                        </span>
                      )}

                      {currentEvent.event_Location && (
                        <span className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 shadow-sm border border-white/60">
                          <i className="fa-solid fa-location-dot text-rose-600"></i>
                          <span className="font-medium">{currentEvent.event_Location}</span>
                        </span>
                      )}
                    </div>

                    <p className="mt-5 text-sm md:text-base text-gray-600 leading-relaxed line-clamp-3">
                      {currentEvent.event_Description || 'Join us for this special event'}
                    </p>

                    <div className="mt-7 flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={() => handleRegister(currentEvent)}
                        className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-indigo-600 via-purple-600 to-amber-500 px-6 py-3 text-sm font-semibold text-white shadow-lg hover:shadow-xl transition-shadow"
                      >
                        Register for Pooja
                      </button>
                      <button
                        type="button"
                        onClick={() => handleEventSelect(currentEvent)}
                        className="inline-flex items-center justify-center rounded-full bg-white/90 hover:bg-white px-6 py-3 text-sm font-semibold text-gray-800 border border-gray-200 shadow-sm hover:shadow-md transition-all"
                      >
                        View Event Details
                      </button>
                    </div>

                    {(() => {
                      const coordinators = getCoordinatorDetails(currentEvent);
                      if (!coordinators.length) return null;
                      return (
                        <div className="mt-6 rounded-2xl bg-white/90 border border-gray-200 shadow-sm p-4">
                          <div className="text-sm font-semibold text-gray-900 mb-3">Coordinators</div>
                          <div className="flex flex-wrap gap-2">
                            {coordinators.map((coordinator, idx) => (
                              <div key={`${coordinator.name}-${idx}`} className="relative group inline-block">
                                <span
                                  className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border cursor-help ${
                                    idx % 3 === 0
                                      ? 'bg-indigo-50 text-indigo-700 border-indigo-100'
                                      : idx % 3 === 1
                                      ? 'bg-purple-50 text-purple-700 border-purple-100'
                                      : 'bg-amber-50 text-amber-800 border-amber-100'
                                  }`}
                                >
                                  {coordinator.name}
                                </span>
                                {(coordinator.email || coordinator.phone || coordinator.description) && (
                                  <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-[100] pointer-events-none">
                                    <div className="bg-white text-gray-800 rounded-lg p-2 shadow-2xl border border-gray-200 w-[180px]">
                                      <div className="font-semibold mb-1.5 text-[10px] text-gray-900 border-b border-gray-200 pb-1 break-words" style={{wordBreak: 'break-word', overflowWrap: 'anywhere'}}>{coordinator.name}</div>
                                      {coordinator.email && (
                                        <div className="flex items-start gap-1 mb-1">
                                          <i className="fas fa-envelope mt-0.5 text-indigo-600 flex-shrink-0 text-[8px]"></i>
                                          <span className="text-gray-700 text-[9px] leading-tight break-all" style={{wordBreak: 'break-all', overflowWrap: 'anywhere'}}>{coordinator.email}</span>
                                        </div>
                                      )}
                                      {coordinator.phone && (
                                        <div className="flex items-start gap-1 mb-1">
                                          <i className="fas fa-phone mt-0.5 text-green-600 flex-shrink-0 text-[8px]"></i>
                                          <span className="text-gray-700 text-[9px] leading-tight">{coordinator.phone}</span>
                                        </div>
                                      )}
                                      {coordinator.description && (
                                        <div className="mt-1.5 pt-1.5 border-t border-gray-200 text-gray-600 text-[8px] leading-snug break-words" style={{wordBreak: 'break-word', overflowWrap: 'anywhere'}}>
                                          {coordinator.description}
                                        </div>
                                      )}
                                      <div className="absolute top-full left-3 -mt-1">
                                        <div className="border-[5px] border-transparent border-t-white" style={{filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.1))'}}></div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                <div className="relative overflow-hidden rounded-[28px] bg-white/75 backdrop-blur-xl shadow-[0_26px_70px_-45px_rgba(15,23,42,0.55)] border border-white/60">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-indigo-50/25 to-purple-50/25"></div>
                  <div className="relative p-5 md:p-6">
                    <div className="flex items-center justify-between gap-3">
                      <div className="inline-flex items-center rounded-full bg-white/85 px-3 py-1 text-xs font-semibold text-gray-700 shadow-sm border border-white/60">
                        Featured Flyer
                      </div>
                      <div className="inline-flex items-center rounded-full bg-white/85 px-3 py-1 text-xs font-semibold text-gray-700 shadow-sm border border-white/60">
                        {currentEvent.event_Name?.split(' ')?.slice(0, 2)?.join(' ') || 'Event'}
                      </div>
                    </div>

                    {(currentEvent.event_Flyer_URL || currentEvent.event_Image_URL) ? (
                      <div className="mt-4 relative overflow-hidden rounded-3xl">
                        <div className="pointer-events-none absolute inset-0">
                          <div className="absolute -left-10 top-12 h-28 w-28 rounded-full bg-indigo-500/25 blur-2xl"></div>
                          <div className="absolute right-0 top-0 h-32 w-32 rounded-full bg-purple-500/25 blur-2xl"></div>
                          <div className="absolute right-10 bottom-10 h-32 w-32 rounded-full bg-amber-400/25 blur-2xl"></div>
                        </div>

                        <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-amber-500">
                          <img
                            src={(currentEvent.event_Flyer_URL || currentEvent.event_Image_URL)?.trim()}
                            alt={currentEvent.event_Name}
                            className="w-full h-auto object-contain opacity-90"
                            onError={(e) => {
                              if (e.target.src === currentEvent.event_Flyer_URL?.trim() && currentEvent.event_Image_URL) {
                                e.target.src = currentEvent.event_Image_URL.trim();
                              } else {
                                e.target.src = 'https://via.placeholder.com/800x600?text=Event';
                              }
                            }}
                          />
                        </div>

                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/25 via-black/5 to-transparent"></div>
                      </div>
                    ) : (
                      <div className="mt-4 rounded-3xl bg-gradient-to-br from-indigo-600 via-purple-600 to-amber-500 p-10 text-white/90">
                        Flyer not available
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="mt-10">
              <div className="mb-5">
                <h2 className="text-base font-semibold text-gray-900">Upcoming Events</h2>
                <p className="mt-1 text-sm text-gray-600">Upcoming events shown in date order.</p>
              </div>

              {upcomingEvents.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                  {upcomingEvents.map((event) => (
                    <div
                      key={event.event_Id}
                      className={`item-card group cursor-pointer p-0 overflow-hidden rounded-[26px] bg-white/80 backdrop-blur-sm border border-gray-100 shadow-[0_14px_45px_-35px_rgba(15,23,42,0.55)] hover:shadow-[0_26px_70px_-45px_rgba(15,23,42,0.55)] transition-all duration-300 hover:-translate-y-0.5 ${
                        selectedEvent?.event_Id === event.event_Id ? 'selected' : ''
                      }`}
                      onClick={() => handleEventSelect(event)}
                    >
                      {selectedEvent?.event_Id === event.event_Id && (
                        <div className="absolute top-4 right-4 z-20 w-9 h-9 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg">
                          <svg className="w-5 h-5 text-white font-bold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}

                      {(event.event_Flyer_URL || event.event_Image_URL) && (
                        <div className="p-4 pb-0">
                          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-400 to-pink-400">
                            <img
                              src={(event.event_Flyer_URL || event.event_Image_URL)?.trim()}
                              alt={event.event_Name}
                              className="w-full h-auto object-contain transition-transform duration-500 group-hover:scale-105"
                              onError={(e) => {
                                if (e.target.src === event.event_Flyer_URL?.trim() && event.event_Image_URL) {
                                  e.target.src = event.event_Image_URL.trim();
                                } else {
                                  e.target.src = 'https://via.placeholder.com/400x300?text=Event';
                                }
                              }}
                            />
                            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-black/5 to-transparent"></div>
                          </div>
                        </div>
                      )}

                      <div className="px-5 pb-5 pt-4 relative z-10">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 leading-snug tracking-tight">
                          {event.event_Name}
                        </h3>

                        {event.event_Date && (
                          <div className="mb-3">
                            <div className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-sm font-semibold text-indigo-700">
                              <i className="fa-solid fa-calendar-days"></i>
                              <span>{formatDate(event.event_Date)}</span>
                            </div>
                          </div>
                        )}

                        {eventTimeOptions[event.event_Id]?.length > 0 && (
                          <div className="mb-3 flex flex-wrap gap-2">
                            {eventTimeOptions[event.event_Id]
                              .filter(timeOption => timeOption.time_Option_Name?.toLowerCase() !== "not sure")
                              .map((timeOption, idx) => {
                              const formatTime = (dateTimeStr) => {
                                if (!dateTimeStr) return '';
                                const date = new Date(dateTimeStr);
                                return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                              };
                              const startTime = formatTime(timeOption.date_Time_From);
                              const endTime = formatTime(timeOption.date_Time_To);
                              
                              return (
                                <div key={idx} className="inline-flex items-center gap-2 rounded-full bg-purple-50 px-3 py-1 text-xs font-semibold text-purple-700">
                                  <i className="fa-regular fa-clock"></i>
                                  <span>
                                    {timeOption.time_Option_Name}
                                    {startTime && endTime && ` (${startTime} - ${endTime})`}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        <p className="text-gray-600 mb-5 text-sm leading-relaxed line-clamp-3">
                          {event.event_Description || 'Join us for this special event'}
                        </p>

                        {event.event_Location && (
                          <p className="text-xs text-gray-500 mb-4 line-clamp-1">
                            <i className="fa-solid fa-location-dot mr-1 text-rose-500"></i>
                            {event.event_Location}
                          </p>
                        )}

                        <div className="mt-5 flex items-center gap-3">
                          <button 
                            onClick={() => {
                              dispatch(setSelectedEvent(event));
                              navigate('/event-info', { state: { scrollToFlyer: true } });
                            }}
                            className="flex-1 bg-white hover:bg-gray-50 text-gray-800 font-semibold py-2.5 px-4 rounded-full border border-gray-200 transition shadow-sm hover:shadow-md text-sm"
                          >
                            View Details
                          </button>
                          <button className="bg-gradient-to-r from-indigo-600 via-purple-600 to-amber-500 hover:from-indigo-700 hover:via-purple-700 hover:to-amber-600 text-white font-semibold py-2.5 px-5 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 text-sm">
                            Register
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-2xl bg-white/70 backdrop-blur border border-white/60 shadow-sm px-6 py-5 text-sm text-gray-600">
                  No upcoming events available.
                </div>
              )}
            </div>
          </div>
        )}

        {!loading && events.length === 0 && !error && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No events available at {selectedCenter?.center_Name}.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventSelection;
